import { useEffect, useMemo, useState } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TooltipContentProps } from "recharts";
import { useTranslation } from "react-i18next";
import { cn, Icon, Skeleton } from "@nexus/ui";
import { usePrefersReducedMotion } from "@/features/overview/components/OverviewChart/usePrefersReducedMotion";
import type { EventJobTimeseries } from "@/features/events/api/events.api";
import type { EventsChartLatencyMetric } from "@/features/admin/lib/events-chart-metric";
import { formatEventWindowShort } from "../constants/event-time-ranges";
import {
  buildEventActivitySeries,
  shouldShowBudgetLine,
  type EventActivityPoint,
} from "../lib/event-activity-series";
import { formatCompactDurationMs, formatPreciseDurationMs } from "../lib/event-timing";

export interface EventActivityRange {
  /** ISO bounds, both inclusive, matching the list API's gte/lte. */
  from: string;
  to: string;
}

interface EventsActivityChartProps {
  data: EventJobTimeseries | undefined;
  isLoading: boolean;
  /**
   * Which latency to plot. `both` is the informative one: end-to-end over run
   * duration, where the gap between the lines is the queue. A run line alone is
   * what made this panel look healthy through a twenty-minute wait.
   */
  latencyMetric: EventsChartLatencyMetric;
  /**
   * The default slow-action budget in ms, drawn across the chart when it is
   * near enough to the data to be worth seeing. Null switches it off.
   */
  budgetMs: number | null;
  /**
   * True while TanStack is holding the previous window via `keepPreviousData`.
   * The entrance grow waits for the selected lookback's own payload — arming on
   * the chip click alone would grow the stale series, latch, then paint the real
   * one with no motion.
   */
  isPlaceholderData: boolean;
  /**
   * Operator-selected lookback from the day/time range chips. Changing it is the
   * one filter that replaces the whole window, so it re-arms the entrance grow
   * once matching (non-placeholder) data arrives.
   */
  windowHours: number;
  /** The range currently filtering the table, so its bar stays lit. */
  selectedRange: EventActivityRange | null;
  onSelectRange: (range: EventActivityRange | null) => void;
}

/**
 * Deliberately shorter than the design system's 320px analytics charts: this
 * panel's job is the event table, and a full-height chart pushes it off a laptop
 * screen. At this height the shape of the window still reads, which is all the
 * chart is here to give.
 */
const CHART_HEIGHT_PX = 132;
const COUNT_AXIS_WIDTH = 30;
/** Wide enough for the longest single-unit duration, "999ms", at 10px. */
const LATENCY_AXIS_WIDTH = 40;
/**
 * Top tick labels sit vertically centered on the domain max. Half the glyph
 * hangs above that point, and the collapse shell is `overflow-hidden`, so a
 * tiny top margin clips labels like "1m 40s". Keep this above half the tick
 * font size; axis `padding.top` also pulls the max tick off the edge.
 */
const CHART_TOP_MARGIN = 16;
const CHART_RIGHT_MARGIN = 4;
/** Insets the scale so the max tick is not flush with the SVG top edge. */
const Y_AXIS_TOP_PADDING = 8;
/** Every bar can be hovered; only every other label has room to be printed. */
const AXIS_MIN_TICK_GAP = 28;
/** A committed filter pushes every other hour well back. */
const UNSELECTED_BAR_OPACITY = 0.25;
/**
 * The one step between focused and receded, reached from either side: hovering
 * an unfiltered chart fades the other hours down to it, and hovering while a
 * filter is committed lifts that hour up to it. Hover previews the same dim a
 * click commits, pitched softer because it is not a commitment yet.
 */
const HOVER_PREVIEW_OPACITY = 0.6;

/**
 * Recharts moves the tooltip between buckets over 400ms of `ease` by default,
 * which reads as the tooltip being stuck to the bucket the operator just left.
 * Its own `isAnimationActive: "auto"` already honors reduced motion, so unlike
 * everything else here this one needs no gate.
 */
const TOOLTIP_TRACK_MS = 150;

/**
 * Bars grow from the baseline via CSS (see `.chart-grow-enter`). Recharts'
 * own bar animation latches mid-flight: its `JavascriptAnimate` cleanup calls
 * `onAnimationEnd`, and an unstable callback (or any parent re-render) fires
 * that cleanup before the grow is visible — especially on lookback chip changes
 * where query updates re-render the tree. Same approach as ScheduledJobs.
 */
const ENTRANCE_MS = 300;

/** Matches the repo's collapsible sections (see RoleDrawer, CreativeResultCard). */
const COLLAPSE_CLASS =
  "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none";

/**
 * Hovering or selecting an hour dims the other buckets. The dim is a per-bucket
 * `fill-opacity`, so the transition has to reach the rects — the series group's
 * own opacity is not the thing that changes.
 */
const BAR_DIM_TRANSITION_CLASS =
  "[&_path]:transition-[fill-opacity] [&_path]:duration-150 [&_path]:ease-out";

const SERIES_COLORS = {
  completed: "var(--chart-spend)",
  failed: "var(--error)",
  inFlight: "var(--info)",
  p95: "var(--chart-purple)",
  endToEnd: "var(--warning)",
} as const;

/**
 * The end-to-end series is drawn as a filled area under its line, with the run
 * line over it. The band between them is the waiting — the delta this whole
 * chart change exists to make visible. Kept faint: it is context for the two
 * lines, not a third thing to read.
 */
const END_TO_END_FILL_OPACITY = 0.12;

// Design tokens are hex values — use var() directly; hsl(var(...)) resolves to black in SVG.
const AXIS_TICK = { fontSize: 10, fill: "var(--muted-foreground)" } as const;
const LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";

function isSameRange(point: EventActivityPoint, range: EventActivityRange | null): boolean {
  return range != null && range.from === point.bucketStart;
}

/**
 * Every bucket is clickable, so pointing at one has to show what clicking would
 * do: the pointed hour holds focus and the rest recede, the same move a click
 * makes permanent. A committed filter outranks the pointer — the selected hour
 * keeps focus and hover only lifts the hour being compared against it.
 */
function resolveBarOpacity(
  point: EventActivityPoint,
  selectedRange: EventActivityRange | null,
  hoveredBucketStart: string | null,
): number {
  const isHovered = point.bucketStart === hoveredBucketStart;

  if (selectedRange != null) {
    if (isSameRange(point, selectedRange)) return 1;
    return isHovered ? HOVER_PREVIEW_OPACITY : UNSELECTED_BAR_OPACITY;
  }

  if (hoveredBucketStart == null) return 1;
  return isHovered ? 1 : HOVER_PREVIEW_OPACITY;
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="size-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className={LABEL_CLASS}>{label}</span>
    </span>
  );
}

function ActivityTooltip({ active, payload }: TooltipContentProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as EventActivityPoint | undefined;
  if (!point) return null;

  const rows: Array<{ key: string; label: string; value: string; color?: string }> = [
    {
      key: "completed",
      label: t("admin.events.activity.completed", "Completed"),
      value: point.completed.toLocaleString(i18n.language),
      color: SERIES_COLORS.completed,
    },
  ];

  if (point.failed > 0) {
    rows.push({
      key: "failed",
      label: t("admin.events.activity.failed", "Failed"),
      value: `${point.failed.toLocaleString(i18n.language)}${
        point.failureRate == null
          ? ""
          : ` · ${point.failureRate.toLocaleString(i18n.language, {
              maximumFractionDigits: 1,
            })}%`
      }`,
      color: SERIES_COLORS.failed,
    });
  }

  if (point.inFlight > 0) {
    rows.push({
      key: "inFlight",
      label: t("admin.events.activity.inFlight", "In flight"),
      value: point.inFlight.toLocaleString(i18n.language),
      color: SERIES_COLORS.inFlight,
    });
  }

  if (point.p95EndToEndMs != null) {
    rows.push({
      key: "endToEnd",
      label: t("admin.events.activity.p95EndToEnd", "p95 end to end"),
      value: formatPreciseDurationMs(point.p95EndToEndMs),
      color: SERIES_COLORS.endToEnd,
    });
  }

  // Between the two lines, so the tooltip reads in the order the time is spent
  // rather than making the operator subtract one line from the other.
  if (point.p95QueueWaitMs != null) {
    rows.push({
      key: "queueWait",
      label: t("admin.events.activity.p95QueueWait", "p95 queued"),
      value: formatPreciseDurationMs(point.p95QueueWaitMs),
    });
  }

  if (point.p95RunMs != null) {
    rows.push({
      key: "p95",
      label: t("admin.events.activity.p95Run", "p95 running"),
      value: formatPreciseDurationMs(point.p95RunMs),
      color: SERIES_COLORS.p95,
    });
  }

  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-foreground">{point.rangeLabel}</p>
      {point.total === 0 ? (
        <p className="text-muted-foreground">
          {t("admin.events.activity.emptyBucket", "No events")}
        </p>
      ) : (
        <ul className="space-y-0.5">
          {rows.map((row) => (
            <li key={row.key} className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
              />
              <span className="text-muted-foreground">{row.label}</span>
              <span className="ml-auto pl-3 font-medium tabular-nums text-foreground">
                {row.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Bucketed volume by outcome with p95 run duration over it, across the same
 * window the KPI strip totals. The strip says how many failed; this says whether
 * they were one bad stretch or a steady bleed, and whether the pipeline slowed
 * down at the same time — the difference between an incident and a bad consumer.
 */
export function EventsActivityChart({
  data,
  isLoading,
  isPlaceholderData,
  latencyMetric,
  budgetMs,
  windowHours,
  selectedRange,
  onSelectRange,
}: EventsActivityChartProps) {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Recharts' built-in grow is not used (see ENTRANCE_MS). Re-arm the CSS
  // entrance only when the lookback chip's own payload lands — not on the chip
  // click (that still shows keepPreviousData) and not on background refetches
  // or typeahead filters for the same window.
  const dataMatchesRequest =
    !isPlaceholderData && data != null && data.windowHours === windowHours;

  const [hasGrown, setHasGrown] = useState(false);
  const [armedWindow, setArmedWindow] = useState<number | null>(null);
  const [entranceEpoch, setEntranceEpoch] = useState(0);
  const [hoveredBucketStart, setHoveredBucketStart] = useState<string | null>(null);

  if (dataMatchesRequest && armedWindow !== windowHours) {
    setArmedWindow(windowHours);
    setHasGrown(false);
    setEntranceEpoch(entranceEpoch + 1);
    setHoveredBucketStart(null);
  }

  const isEntranceActive = !prefersReducedMotion && !hasGrown && dataMatchesRequest;

  useEffect(() => {
    if (!isEntranceActive) return;
    const id = window.setTimeout(() => setHasGrown(true), ENTRANCE_MS + 40);
    return () => window.clearTimeout(id);
  }, [isEntranceActive, entranceEpoch]);

  const series = useMemo(
    () => buildEventActivitySeries(data, i18n.language),
    [data, i18n.language],
  );

  const worst = series.worstBucket;

  // A series is drawn only when the setting asks for it *and* the window has
  // something to draw, so switching the setting on never produces a line lying
  // flat along zero where the data is simply absent.
  const showEndToEnd = latencyMetric !== "run" && series.hasEndToEndLatency;
  const showRun = latencyMetric !== "endToEnd" && series.hasRunLatency;
  const hasLatencyAxis = showEndToEnd || showRun;
  const showBudget =
    hasLatencyAxis && shouldShowBudgetLine(budgetMs, series.maxLatencyMs);

  // Recharts hands back the active index rather than the datum, and the bucket
  // under the pointer may be an empty one — still a real answer, so it resolves
  // like any other rather than being treated as a miss. `null` is recharts'
  // "no active bucket" and has to be ruled out before `Number` turns it into 0.
  const pointAt = (
    activeIndex: number | string | null | undefined,
  ): EventActivityPoint | undefined => {
    if (activeIndex == null) return undefined;
    const index = Number(activeIndex);
    return Number.isInteger(index) ? series.points[index] : undefined;
  };

  const summary = t(
    "admin.events.activity.summary",
    "Event volume over the last {{window}}: {{total}} total, {{failed}} failed.",
    {
      window: formatEventWindowShort(windowHours, t),
      total: series.total.toLocaleString(i18n.language),
      failed: series.failed.toLocaleString(i18n.language),
    },
  );

  return (
    <section className="shrink-0 rounded-md border border-border bg-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setIsExpanded((open) => !open)}
          aria-expanded={isExpanded}
          aria-controls="admin-events-activity-chart"
          className="inline-flex items-center gap-1 rounded-sm text-left transition-colors duration-150 ease-out hover:text-foreground motion-reduce:transition-none"
        >
          <Icon
            name="chevron-down"
            size="xs"
            aria-hidden
            className={cn(
              // Same 200ms as the panel, so the arrow turns while the chart moves
              // rather than beating it there.
              "text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none",
              !isExpanded && "-rotate-90",
            )}
          />
          <span className={LABEL_CLASS}>{t("admin.events.activity.title", "Activity")}</span>
        </button>

        {worst ? (
          <button
            type="button"
            onClick={() =>
              onSelectRange(
                isSameRange(worst, selectedRange)
                  ? null
                  : { from: worst.bucketStart, to: worst.bucketEnd },
              )
            }
            aria-pressed={isSameRange(worst, selectedRange)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-[11px] transition-colors duration-150 ease-out hover:bg-foreground/5 motion-reduce:transition-none",
              // Mounts the moment a clean window starts failing, and only then —
              // it survives refetches that change which bucket is worst. Sliding
              // it in spends the one attention-grab this row is entitled to on the
              // thing that just went wrong.
              "motion-safe:animate-in fade-in slide-in-from-left-1 animation-duration-200",
              isSameRange(worst, selectedRange) && "bg-error-subtle hover:bg-error-subtle",
            )}
          >
            <Icon name="alert-circle" size="xs" colorVar="--error" aria-hidden />
            <span className="text-foreground">
              {t("admin.events.activity.peak", "{{count}} failed at {{time}}", {
                count: worst.failed,
                time: worst.label,
              })}
            </span>
          </button>
        ) : null}

        <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
          <LegendDot
            color={SERIES_COLORS.completed}
            label={t("admin.events.activity.completed", "Completed")}
          />
          <LegendDot
            color={SERIES_COLORS.failed}
            label={t("admin.events.activity.failed", "Failed")}
          />
          <LegendDot
            color={SERIES_COLORS.inFlight}
            label={t("admin.events.activity.inFlight", "In flight")}
          />
          {showEndToEnd ? (
            <LegendDot
              color={SERIES_COLORS.endToEnd}
              label={t("admin.events.activity.p95EndToEnd", "p95 end to end")}
            />
          ) : null}
          {showRun ? (
            <LegendDot
              color={SERIES_COLORS.p95}
              label={t("admin.events.activity.p95Run", "p95 running")}
            />
          ) : null}
        </div>
      </div>

      {/*
        Collapsing through grid rows keeps the chart mounted, so the panel gives
        the table its space back smoothly instead of the rows jumping up. `inert`
        is what makes staying mounted safe: recharts renders a tabbable
        `role="application"` surface that would otherwise sit in the tab order
        behind a closed panel.
      */}
      <div
        id="admin-events-activity-chart"
        inert={!isExpanded || undefined}
        className={cn(COLLAPSE_CLASS, isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}
      >
        {/* `overflow-hidden` is load-bearing, not cosmetic: it makes this a scroll
            container, which is what lets a grid item shrink past its content.
            It also has to stay padding-free — a border-box element cannot shrink
            below its own padding, so padding here would leave the closed panel
            propped open by that much. */}
          <div className="overflow-hidden">
          <div className="px-1 pt-1 pb-2">
            {isLoading && series.points.length === 0 ? (
              <Skeleton className="w-full rounded-sm" style={{ height: CHART_HEIGHT_PX }} />
            ) : series.total === 0 ? (
              <div
                className="flex items-center justify-center text-xs text-muted-foreground"
                style={{ height: CHART_HEIGHT_PX }}
              >
                {t("admin.events.activity.empty", "No events in this window")}
              </div>
            ) : (
              <div
                // Remount when a matching lookback payload arms the entrance so
                // the CSS grow runs on the real series, not a keepPreviousData
                // holdover. Class is removed after ENTRANCE_MS via the latch.
                key={entranceEpoch}
                className={cn(isEntranceActive && "chart-grow-enter")}
              >
                <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
                  <ComposedChart
                    data={series.points}
                    margin={{ top: CHART_TOP_MARGIN, right: CHART_RIGHT_MARGIN, left: 0, bottom: 0 }}
                    title={summary}
                    onClick={(state) => {
                      const point = pointAt(state?.activeTooltipIndex);
                      if (!point) return;
                      onSelectRange(
                        isSameRange(point, selectedRange)
                          ? null
                          : { from: point.bucketStart, to: point.bucketEnd },
                      );
                    }}
                    onMouseMove={(state) => {
                      // Held back until the bars have finished growing: dimming
                      // mid-entrance would fight the CSS scale with opacity
                      // transitions under the pointer.
                      if (isEntranceActive) return;
                      const next = pointAt(state?.activeTooltipIndex)?.bucketStart ?? null;
                      // Fires on every pointer move; returning the same value lets
                      // React skip the re-render for moves within one bucket.
                      setHoveredBucketStart((current) => (current === next ? current : next));
                    }}
                    onMouseLeave={() => setHoveredBucketStart(null)}
                    className="cursor-pointer"
                  >
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={AXIS_TICK}
                      tickLine={false}
                      axisLine={{ stroke: "var(--border)" }}
                      interval="preserveStartEnd"
                      minTickGap={AXIS_MIN_TICK_GAP}
                    />
                    <YAxis
                      yAxisId="count"
                      tick={AXIS_TICK}
                      tickLine={false}
                      axisLine={false}
                      width={COUNT_AXIS_WIDTH}
                      allowDecimals={false}
                      padding={{ top: Y_AXIS_TOP_PADDING, bottom: 0 }}
                    />
                    {hasLatencyAxis ? (
                      <YAxis
                        yAxisId="latency"
                        orientation="right"
                        tick={{
                          ...AXIS_TICK,
                          fill: showEndToEnd ? SERIES_COLORS.endToEnd : SERIES_COLORS.p95,
                        }}
                        tickLine={false}
                        axisLine={false}
                        width={LATENCY_AXIS_WIDTH}
                        tickFormatter={(value: number) => formatCompactDurationMs(value)}
                        padding={{ top: Y_AXIS_TOP_PADDING, bottom: 0 }}
                      />
                    ) : null}
                    <Tooltip
                      content={(props) => (
                        <ActivityTooltip {...(props as TooltipContentProps<number, string>)} />
                      )}
                      cursor={{ fill: "var(--foreground)", fillOpacity: 0.04 }}
                      animationDuration={TOOLTIP_TRACK_MS}
                      animationEasing="ease-out"
                    />
                    {(["completed", "failed", "inFlight"] as const).map((key) => (
                      <Bar
                        key={key}
                        yAxisId="count"
                        dataKey={key}
                        stackId="outcome"
                        fill={SERIES_COLORS[key]}
                        className={prefersReducedMotion ? undefined : BAR_DIM_TRANSITION_CLASS}
                        // Recharts JS grow is disabled — CSS `.chart-grow-enter`
                        // owns the entrance (see ENTRANCE_MS).
                        isAnimationActive={false}
                      >
                        {series.points.map((point) => (
                          <Cell
                            key={point.bucketStart}
                            // Selecting an hour dims the rest, so the filtered slice
                            // is visible in the chart and not just in the table.
                            fillOpacity={resolveBarOpacity(
                              point,
                              selectedRange,
                              hoveredBucketStart,
                            )}
                          />
                        ))}
                      </Bar>
                    ))}
                    {/* Drawn before the run line so the run line sits over the
                        fill rather than under it, which is what makes the band
                        between them read as the waiting. */}
                    {showEndToEnd ? (
                      <Area
                        yAxisId="latency"
                        type="monotone"
                        dataKey="p95EndToEndMs"
                        stroke={SERIES_COLORS.endToEnd}
                        strokeWidth={1.5}
                        fill={SERIES_COLORS.endToEnd}
                        fillOpacity={END_TO_END_FILL_OPACITY}
                        dot={false}
                        activeDot={{ r: 2.5, strokeWidth: 0 }}
                        isAnimationActive={false}
                        connectNulls
                      />
                    ) : null}
                    {showRun ? (
                      <Line
                        yAxisId="latency"
                        type="monotone"
                        dataKey="p95RunMs"
                        stroke={SERIES_COLORS.p95}
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={{ r: 2.5, strokeWidth: 0 }}
                        isAnimationActive={false}
                        connectNulls
                      />
                    ) : null}
                    {showBudget ? (
                      <ReferenceLine
                        yAxisId="latency"
                        y={budgetMs ?? undefined}
                        stroke="var(--error)"
                        strokeDasharray="4 3"
                        strokeWidth={1}
                        ifOverflow="hidden"
                        label={{
                          value: t("admin.events.activity.budget", "Budget"),
                          position: "insideTopRight",
                          fontSize: 9,
                          fill: "var(--error)",
                        }}
                      />
                    ) : null}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
